import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from '@/components/router';
import { AuthContextType, AuthState, LoginFormData } from '@/features/auth/types';
import { authService } from '@/features/auth/services/auth-api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState['authState']>(null as any);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [loginData, setLoginData] = useState<LoginFormData | null>(null);
  const [userName, setUserName] = useState<string>("");

  // Check for existing session on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // First check server session (this works across tabs)
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.user) {
            // User is authenticated via session cookie
            setAuthState("dashboard");
            setUserName(response.user.displayName);
            setLoginData({
              codicePersona: response.user.codicePersona,
              password: "" // Password not needed for display
            });
            return;
          }
        } catch (apiError) {
          console.log("Server session check failed, trying sessionStorage");
        }

        // If server session check fails, check sessionStorage for current tab state
        const savedAuthState = sessionStorage.getItem('authState');
        const savedLoginData = sessionStorage.getItem('loginData');
        const savedUserName = sessionStorage.getItem('userName');

        if (savedAuthState) {
          setAuthState(savedAuthState as AuthState['authState']);
          if (savedLoginData) {
            setLoginData(JSON.parse(savedLoginData));
          }
          if (savedUserName) {
            setUserName(savedUserName);
          }
          return;
        }

        // If no session found, show login
        setAuthState("login");
      } catch (error) {
        console.error("Session check failed:", error);
        setAuthState("login");
      }
    };

    checkSession();
  }, []);

  // Save auth state to sessionStorage whenever it changes
  useEffect(() => {
    if (authState) {
      sessionStorage.setItem('authState', authState);
      if (loginData) {
        sessionStorage.setItem('loginData', JSON.stringify(loginData));
      }
      if (userName) {
        sessionStorage.setItem('userName', userName);
      }
    }
  }, [authState, loginData, userName]);

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await authService.login(data);

      if (!result.success) {
        throw new Error(result.error || "Authentication failed");
      }

      // If 2FA is required, show OTP page
      if (result.requires2FA) {
        setLoginData(data);
        setUserName("");
        setAuthState("otp");
      } else {
        // Login successful, go to dashboard
        setUserName(result.user?.displayName || "");
        setAuthState("dashboard");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (otpCode: string) => {
    if (!loginData) return;

    setIsLoading(true);
    setError("");

    try {
      const result = await authService.submitOTP(loginData.codicePersona, otpCode);

      if (!result.success) {
        throw new Error(result.error || "OTP verification failed");
      }

      // OTP successful, go to dashboard
      setUserName(result.user?.displayName || "");
      setAuthState("dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "OTP verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setAuthState("login");
    setLoginData(null);
    setUserName("");
    setError("");
    // Clear sessionStorage
    sessionStorage.removeItem('authState');
    sessionStorage.removeItem('loginData');
    sessionStorage.removeItem('userName');
  };

  const handleLoginError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleLogout = async () => {
    await authService.logout();
    setAuthState("login");
    setLoginData(null);
    setUserName("");
    setError("");
    // Clear sessionStorage
    sessionStorage.removeItem('authState');
    sessionStorage.removeItem('loginData');
    sessionStorage.removeItem('userName');
  };

  const user = authState === "dashboard" ? {
    id: "1", // This would come from the authentication response
    codicePersona: loginData?.codicePersona || "",
    displayName: userName || "User",
  } : null;

  const contextValue: AuthContextType = {
    authState,
    user,
    isLoading,
    error,
    loginData,
    userName,
    handleLogin,
    handleOTPSubmit,
    handleBackToLogin,
    handleLoginError,
    handleLogout,
    setAuthState,
    setUserName,
    setError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Navigation component that handles auth state routing
export function AuthNavigator() {
  const { authState } = useAuth();
  const { navigate } = useRouter();

  // Navigate to appropriate route based on auth state
  useEffect(() => {
    if (authState === "dashboard" && window.location.pathname !== "/dashboard") {
      navigate("/dashboard");
    } else if (authState === "otp" && window.location.pathname !== "/otp") {
      navigate("/otp");
    } else if (authState === "login" &&
              !["/", "/login"].includes(window.location.pathname)) {
      navigate("/login");
    }
  }, [authState, navigate]);

  return null; // This component only handles navigation, doesn't render anything
}